import React, { useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import { Autoplay, EffectCoverflow, Navigation, Pagination, Mousewheel, Keyboard } from "swiper/modules";
import { getNew10Games } from "../Redux/Slice/game.slice";
import { useDispatch, useSelector } from "react-redux";
import CardCarouselSkeleton from "./skeleton-loaders/CardCarouselSkeleton";

const CardCarousel = ({
  images,
  autoplayDelay = 2000,
  showPagination = true,
  showNavigation = true,
}) => {
  const css =
    `
    .new-games-carousel .swiper {
      width: 100%;
      padding-bottom: 50px;
      padding-top: 50px;
    }
    .new-games-carousel .swiper-slide {
      background-position: center;
      background-size: cover;
      width: 500px;
    }
    .new-games-carousel .swiper-slide img {
      display: block;
      width: 100%;
      border-radius: 16px;
      transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1),
                  box-shadow 600ms cubic-bezier(0.22, 1, 0.36, 1),
                  opacity 600ms ease,
                  clip-path 600ms cubic-bezier(0.22, 1, 0.36, 1);
      will-change: transform, clip-path;
    }

    /* Distinct shapes for immediate prev/next slides */
    .new-games-carousel .swiper-slide-next {
      transform:translate3d(0px, 0px, -282px)  rotateY(-40deg) scale(1) !important;
    }
    .new-games-carousel .swiper-slide-prev {
      transform:translate3d(0px, 0px, -282px)  rotateY(40deg) scale(1) !important
    }
    .new-games-carousel .swiper-slide-active img {
      opacity: 1;
      transform: scale(1.04);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
      animation: floatY 6s ease-in-out infinite;
      /* Center card keeps original rectangular shape */
      -webkit-clip-path: none;
      clip-path: none;
    }
    @keyframes floatY {
      0% { transform: translateY(0) scale(1.04); }
      50% { transform: translateY(-8px) scale(1.06); }
      100% { transform: translateY(0) scale(1.04); }
    }
    .new-games-carousel .swiper-3d .swiper-slide-shadow-left {
      background-image: none;
    }
    .new-games-carousel .swiper-3d .swiper-slide-shadow-right{
      background: none;
    }
    
    /* Responsive adjustments */
    @media (max-width: 1024px) {
      .new-games-carousel .swiper-slide {
        width: 420px;
      }
    }
    @media (max-width: 768px) {
      .new-games-carousel .swiper {
        padding-top: 30px;
        padding-bottom: 30px;
      }
      .new-games-carousel .swiper-slide {
        width: 340px;
      }
    }
    @media (max-width: 640px) {
      .new-games-carousel .swiper {
        padding-top: 24px;
        padding-bottom: 24px;
      }
      .new-games-carousel .swiper-slide {
        width: 260px;
      }
    }

    @media (max-width: 320px) {
      .new-games-carousel .swiper-slide {
        width: 200px;
      }
    }
  `;
  const dispatch = useDispatch();

  const { new10Games, loadingStates } = useSelector((state) => state.game);

  useEffect(() => {
    if (new10Games.length === 0) {
      dispatch(getNew10Games());
    }
  }, [new10Games.length]);

  const displayImages = (Array.isArray(new10Games) && new10Games.length > 0)
    ? new10Games
      .map((g) => ({
        src: g?.cover_image?.url || (Array.isArray(g?.images) && g.images[0]?.url) || null,
        alt: g?.title || "Game image"
      }))
      .filter((img) => Boolean(img.src))
    : [];

  const isLoading = loadingStates?.new10Games;
  const isEmpty = displayImages.length === 0;

  if (isLoading || isEmpty) {
    return (
      <section className="w-full new-games-carousel">
        <style>{css}</style>
        <CardCarouselSkeleton />
      </section>
    );
  }

  return (
    <section className="w-full new-games-carousel">
      <style>{css}</style>
      <div className="mx-auto w-full max-w-[100%] md:max-w-[80%] rounded-[24px] border border-black/5 p-2 shadow-sm md:rounded-t-[44px]">
        <div className="relative mx-auto flex w-full flex-col rounded-[24px] border border-black/5 bg-neutral-800/5 p-2 shadow-sm md:items-start md:gap-8 md:rounded-b-[20px] md:rounded-t-[40px] md:p-2">
          <div className="flex flex-col justify-center pb-2 md:pl-4 pt-2 md:pt-10 md:items-center">
            <div className="flex gap-2 justify-center">
              <div>
                <h3 className="text-lg md:text-4xl font-bold text-white">
                  New Games
                </h3>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-center gap-4">
            <div className="w-full">
              <Swiper
                key={displayImages.length}
                spaceBetween={40}
                speed={900}
                autoplay={{
                  delay: autoplayDelay,
                  pauseOnMouseEnter: true,
                  disableOnInteraction: false,
                }}
                effect={"coverflow"}
                grabCursor={false}
                centeredSlides={true}
                loop={true}
                slidesPerView={"auto"}
                mousewheel={{ forceToAxis: true, releaseOnEdges: true, sensitivity: 0.5 }}
                keyboard={{ enabled: true }}
                coverflowEffect={{
                  rotate: 0,
                  stretch: 0,
                  depth: 100,
                  modifier: 2.5,
                }}
                pagination={showPagination}
                navigation={
                  showNavigation
                    ? {
                      nextEl: ".swiper-button-next",
                      prevEl: ".swiper-button-prev",
                    }
                    : undefined
                }
                modules={[EffectCoverflow, Pagination, Autoplay, Navigation, Mousewheel, Keyboard]}
              >
                {displayImages.map((image, index) => (
                  <SwiperSlide key={`slide1-${index}`}>
                    <div className="size-full rounded md:rounded-3xl bg-[#121015] h-[150px] md:h-[180px] lg:h-[255px]">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="size-full rounded md:rounded-xl w-full h-full object-cover"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CardCarousel;